/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Checkbox } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { isBitSet, getAlarmLedColor } from "../utils";

const SDCU_ALARM_DEFINITIONS: AlarmDefinition[] = [
  { bit: 1, name: "Overall Alarm" },
  { bit: 2, name: "Temperature Alarm" },
  { bit: 3, name: "Power Temperature Alarm" },
  { bit: 4, name: "Digital HW Alarm" },
  { bit: 5, name: "Digital UnClock Alarm" },
  { bit: 6, name: "System Delay Alarm" },
  { bit: 7, name: "Firmware Mismatch Alarm" },
  { bit: 8, name: "Baseline Alarm" },
  { bit: 9, name: "TDD-NR Sync Alarm" },
  { bit: 10, name: "TDD-LTE Sync Alarm" },
  { bit: 11, name: "DL Overload Alarm" },
  { bit: 12, name: "DC Voltage Lower Alarm" },
];

const SFP_ALARM_ROWS: AlarmDefinition[] = [
  { bit: 1, name: "SFP Rx Alarm" },
  { bit: 2, name: "SFP Tx Alarm" },
  { bit: 3, name: "SFP Sync Alarm" },
  { bit: 4, name: "SFP Link Alarm" },
  { bit: 5, name: "SFP Temperature Alarm" },
  { bit: 6, name: "SFP Factory Alarm" },
];

const formatMetricValue = (value: unknown): string => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "N/A";
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }

  if (num <= -99) {
    return "N/A";
  }

  return String(num);
};

interface SdcuRfModule {
  index: string;
  dlPowerIn: unknown;
  ulPowerOut: unknown;
  rfSwitch: unknown;
}

interface SdcuSfpPort {
  sdcuSFPPortNum: string;
  sdcuSFPTemperature?: unknown;
  sdcuSFPTxPower?: unknown;
  sdcuSFPRxPower?: unknown;
  sdcuSFPAlarms?: unknown;
  sdcuSFPAlarmsMask?: unknown;
}


const buildRfModule = (
  value: Record<string, unknown>,
  index: string
): SdcuRfModule => ({
  index,
  dlPowerIn:
    value[`sdcuDlPwrIn${index}`] ??
    value[`dcuDlPwrIn${index}`] ??
    value.sdcuDlPwrIn ??
    value.dcuDlPwrIn,
  ulPowerOut:
    value[`sdcuUlPwrOut${index}`] ??
    value[`dcuUlPwrOut${index}`] ??
    value.sdcuUlPwrOut ??
    value.dcuUlPwrOut,
  rfSwitch:
    value[`sdcuRFSwitch${index}`] ??
    value[`dcuRFSwitch${index}`] ??
    value.sdcuRFSwitch ??
    value.dcuRFSwitch,
});

const normalizeSdcuRfInfo = (rawSdcuRfInfo: unknown): SdcuRfModule[] => {
  if (!rawSdcuRfInfo) {
    return [];
  }

  if (Array.isArray(rawSdcuRfInfo)) {
    return rawSdcuRfInfo
      .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
      .map((item, idx) => {
        const fallbackIndex = String(idx + 1);
        const itemIndex = String(
          item.sdcuRfInfoIndex ?? item.dcuRfInfoIndex ?? fallbackIndex
        );

        return buildRfModule(item, itemIndex);
      })
      .sort((a, b) => Number(a.index) - Number(b.index));
  }

  if (typeof rawSdcuRfInfo !== "object") {
    return [];
  }

  const rfInfoObject = rawSdcuRfInfo as Record<string, unknown>;

  const indexedKeys = Object.keys(rfInfoObject)
    .map((key) => key.match(/(?:sdcu|dcu)(?:DlPwrIn|UlPwrOut|RFSwitch)(\d+)$/)?.[1])
    .filter((index): index is string => !!index);

  const uniqueIndexes = Array.from(new Set(indexedKeys)).sort(
    (a, b) => Number(a) - Number(b)
  );

  if (uniqueIndexes.length > 0) {
    return uniqueIndexes.map((index) => buildRfModule(rfInfoObject, index));
  }

  const objectIndex = String(rfInfoObject.sdcuRfInfoIndex ?? rfInfoObject.dcuRfInfoIndex ?? "1");

  return [buildRfModule(rfInfoObject, objectIndex)];
};

const normalizeSdcuSfpInfo = (rawSdcuSfpInfo: unknown): SdcuSfpPort[] => {
  if (!rawSdcuSfpInfo) {
    return [];
  }

  if (Array.isArray(rawSdcuSfpInfo)) {
    return rawSdcuSfpInfo
      .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
      .map((item, idx) => {
        const fallbackPort = String(idx + 1);

        return {
          sdcuSFPPortNum: String(
            item.dcuSFPPortNum ?? item.sdcuSFPPortNum ?? item.sdcuSFPInfoIndex ?? fallbackPort
          ),
          sdcuSFPTemperature: item.dcuSFPTemperature ?? item.sdcuSFPTemperature,
          sdcuSFPTxPower: item.dcuSFPTxPower ?? item.sdcuSFPTxPower,
          sdcuSFPRxPower: item.dcuSFPRxPower ?? item.sdcuSFPRxPower,
          sdcuSFPAlarms: item.dcuSFPAlarms ?? item.sdcuSFPAlarms,
          sdcuSFPAlarmsMask: item.dcuSFPAlarmsMask ?? item.sdcuSFPAlarmsMask,
        };
      });
  }

  if (typeof rawSdcuSfpInfo !== "object") {
    return [];
  }

  const sfpInfoObject = rawSdcuSfpInfo as Record<string, unknown>;
  const suffixes = Array.from(
    new Set(
      Object.keys(sfpInfoObject)
        .map((key) =>
          key.match(/(?:sdcu|dcu)SFP(?:Temperature|TxPower|RxPower|Alarms|AlarmsMask)-(.+)$/)?.[1]
        )
        .filter((suffix): suffix is string => !!suffix)
    )
  );

  if (suffixes.length > 0) {
    return suffixes.map((suffix) => ({
      sdcuSFPPortNum: suffix,
      sdcuSFPTemperature:
        sfpInfoObject[`sdcuSFPTemperature-${suffix}`] ?? sfpInfoObject[`dcuSFPTemperature-${suffix}`],
      sdcuSFPTxPower:
        sfpInfoObject[`sdcuSFPTxPower-${suffix}`] ?? sfpInfoObject[`dcuSFPTxPower-${suffix}`],
      sdcuSFPRxPower:
        sfpInfoObject[`sdcuSFPRxPower-${suffix}`] ?? sfpInfoObject[`dcuSFPRxPower-${suffix}`],
      sdcuSFPAlarms:
        sfpInfoObject[`sdcuSFPAlarms-${suffix}`] ?? sfpInfoObject[`dcuSFPAlarms-${suffix}`],
      sdcuSFPAlarmsMask:
        sfpInfoObject[`sdcuSFPAlarmsMask-${suffix}`] ?? sfpInfoObject[`dcuSFPAlarmsMask-${suffix}`],
    }));
  }

  return [
    {
      sdcuSFPPortNum: String(sfpInfoObject.sdcuSFPInfoIndex ?? sfpInfoObject.dcuSFPPortNum ?? "1"),
      sdcuSFPTemperature: sfpInfoObject.sdcuSFPTemperature ?? sfpInfoObject.dcuSFPTemperature,
      sdcuSFPTxPower: sfpInfoObject.sdcuSFPTxPower ?? sfpInfoObject.dcuSFPTxPower,
      sdcuSFPRxPower: sfpInfoObject.sdcuSFPRxPower ?? sfpInfoObject.dcuSFPRxPower,
      sdcuSFPAlarms: sfpInfoObject.sdcuSFPAlarms ?? sfpInfoObject.dcuSFPAlarms,
      sdcuSFPAlarmsMask: sfpInfoObject.sdcuSFPAlarmsMask ?? sfpInfoObject.dcuSFPAlarmsMask,
    },
  ];
};

const SDcuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const sDcuAlarms = parsedAttributes.sDcuAlarms ?? parsedAttributes.sdcuAlarms;
  const sDcuAlarmsMask = parsedAttributes.sDcuAlarmsMask ?? parsedAttributes.sdcuAlarmsMask;

  const sDcuTopologyLocation = parsedAttributes.sDcuTopologyLocation ?? parsedAttributes.sdcuTopologyLocation;
  const sDcuEquipmentModel = parsedAttributes.sDcuEquipmentModel ?? parsedAttributes.sdcuEquipmentModel ?? device.model;
  const sDcuEquipmentSN = parsedAttributes.sDcuEquipmentSN ?? parsedAttributes.sdcuEquipmentSN ?? device.serialNumber;
  const sDcuTemperature = parsedAttributes.sdcuEquipmentTemperature ?? parsedAttributes.sdcuEquipmentTemperature;
  const sDcuFirmwareVersion = parsedAttributes.sdcuFirmwareVersion ?? parsedAttributes.sdcuFirmwareVersion;
  const sDcuHardwareVersion = parsedAttributes.sdcuHardwareVersion ?? parsedAttributes.sdcuHardwareVersion;
 
  const sdcuSfpInfo = parsedAttributes.sdcuSFPInfo ?? parsedAttributes.sdcuSfpInfo;
  const sdcuRfInfo = parsedAttributes.sdcuRfInfo;

  const hasAlarms = sDcuAlarms !== undefined || sDcuAlarmsMask !== undefined;
  const hasGeneralInfo =
    sDcuTopologyLocation ||
    sDcuEquipmentModel ||
    sDcuEquipmentSN ||
    sDcuTemperature !== undefined ||
    sDcuFirmwareVersion ||
    sDcuHardwareVersion;

 const isDisconnected = statusLabel === "Disconnected";

  const normalizedSfpInfo = normalizeSdcuSfpInfo(sdcuSfpInfo);
  const sfpPortMap = normalizedSfpInfo.reduce((acc, item) => {
    acc[item.sdcuSFPPortNum] = item;
    return acc;
  }, {} as Record<string, SdcuSfpPort>);

  const sfpPorts = Object.keys(sfpPortMap).sort((a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);

    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      return aNum - bNum;
    }

    return a.localeCompare(b);
  });

  const hasSfpAlarms = sfpPorts.length > 0;
  
  const rfInfoSorted = normalizeSdcuRfInfo(sdcuRfInfo);

  const hasRfInfo = rfInfoSorted.length > 0;

  const renderMetricValueWithUnit = (
    value: unknown,
    unit: string,
    valueClassName = "font-medium"
  ) => {
    const formattedValue = formatMetricValue(value);

    if (formattedValue === "N/A") {
      return <span className="font-medium text-muted-foreground">N/A</span>;
    }

    return (
      <span className={valueClassName}>
        {formattedValue} {unit}
      </span>
    );
  };


  if (alarmsOnly) {
    if (!hasAlarms) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">Alarms</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AlarmTable
            alarms={sDcuAlarms}
            alarmsMask={sDcuAlarmsMask}
            alarmDefinitions={SDCU_ALARM_DEFINITIONS}
            isDisconnected={isDisconnected}
          />
        </CardContent>
      </Card>
    );
  }

  if (!hasGeneralInfo && !hasAlarms && !hasSfpAlarms && !hasRfInfo) return null;

 

  return (
     <div className="space-y-4">
      {hasAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={sDcuAlarms}
              alarmsMask={sDcuAlarmsMask}
              alarmDefinitions={SDCU_ALARM_DEFINITIONS}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}
   
      
    {/* SFP STATUS */}
      {hasSfpAlarms && (
        <Card className="shadow-sm border">
             <CardHeader className="px-4 py-3">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">SFP Status</p>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {sfpPorts.map((port) => {
                const sfp = sfpPortMap[port];

                return (
                  <div
                    key={port}
                    className="rounded-xl border bg-card p-4 shadow-sm"
                  >
                    {/* HEADER */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-semibold">
                        Module {port}
                      </div>

                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            getAlarmLedColor(
                              isDisconnected,
                              Number(sfp?.sdcuSFPAlarms) > 0
                            ),
                        }}
                      />
                    </div>

                    {/* VALUES */}
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          Temperature
                        </span>

                        {renderMetricValueWithUnit(
                          sfp?.sdcuSFPTemperature,
                          "°C"
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          TX Power
                        </span>

                        {renderMetricValueWithUnit(
                          sfp?.sdcuSFPTxPower,
                          "dBm"
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          RX Power
                        </span>

                        {renderMetricValueWithUnit(
                          sfp?.sdcuSFPRxPower,
                          "dBm"
                        )}
                      </div>
                    </div>

                    {/* ALARM MATRIX */}
                    <div className="mt-4 border-t pt-3 space-y-2">
                      {SFP_ALARM_ROWS.map((alarm) => {
                        const isAlarmActive = isBitSet(
                          sfp?.sdcuSFPAlarms,
                          alarm.bit
                        );

                        const isMaskEnabled = isBitSet(
                          sfp?.sdcuSFPAlarmsMask,
                          alarm.bit
                        );

                        return (
                          <div
                            key={alarm.bit}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    getAlarmLedColor(
                                      isDisconnected,
                                      isAlarmActive
                                    ),
                                }}
                              />

                              <span className="text-[11px] truncate text-muted-foreground">
                                {alarm.name}
                              </span>
                            </div>

                            <Checkbox
                              checked={isMaskEnabled}
                              disabled
                              size="small"
                              sx={{
                                padding: 0,
                                transform: "scale(0.7)",
                                color:
                                  "var(--foreground)",
                                "&.Mui-disabled": {
                                  color:
                                    "var(--muted-foreground)",
                                },
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RF MODULES */}
{hasRfInfo && (
  <Card className="shadow-sm border">
     <CardHeader className="px-4 py-3">
          <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">RF Modules</p>
      </CardHeader>

    <CardContent className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {rfInfoSorted.map((rf) => {
          const rfEnabled =
            String(rf.rfSwitch) === "1";

          return (
            <div
              key={rf.index}
              className="rounded-xl border bg-card p-4 shadow-sm"
            >
              {/* HEADER */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-semibold">
                  Module {rf.index}
                </div>

                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        rfEnabled
                          ? "#22c55e"
                          : "#ef4444",
                    }}
                  />

                  <span className="text-xs text-muted-foreground">
                    RF:{" "}
                    {rfEnabled
                      ? "ON"
                      : "OFF"}
                  </span>
                </div>
              </div>

              {/* DL */}
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-sky-500 font-medium">
                    ↓ DL Power
                  </span>

                  {renderMetricValueWithUnit(
                    rf.dlPowerIn,
                    "dBm",
                    "font-medium text-green-600"
                  )}
                </div>

                {/* UL */}
                <div className="flex items-center justify-between">
                  <span className="text-sky-500 font-medium">
                    ↑ UL Power
                  </span>

                  {renderMetricValueWithUnit(
                    rf.ulPowerOut,
                    "dBm",
                    "font-medium text-green-600"
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
)}
    </div>
  );
};

export default SDcuDeviceDetails;
